// src/pages/Entrepreneurs.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, limit, startAfter } from 'firebase/firestore';
import useProtectedRoute from '../hooks/useProtectedRoute';
import Menu from '../components/Menu';
import WeeklyHighlights from '../components/WeeklyHighlights';
import EntrepreneursGrid from '../components/EntrepreneursGrid';
import EntrepreneursFilterBar from '../components/EntrepreneursFilterBar';
import Footer from '../components/Footer';
import './Entrepreneurs.css';
import EntrepreneurDetailsPopup from '../components/EntrepreneurDetailsPopup';

const ITEMS_PER_PAGE = 10;

const Entrepreneurs = () => {
  const isLoggedIn = useProtectedRoute();
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [selectedEntrepreneur, setSelectedEntrepreneur] = useState(null);

  const [authorInfo, setAuthorInfo] = useState(null);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [showHighlightsOnly, setShowHighlightsOnly] = useState(false);

  const fetchAuthorData = async (userId) => {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    return userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
  };

  useEffect(() => {
    if (selectedEntrepreneur) {
      fetchAuthorData(selectedEntrepreneur.userId).then(data => {
        setAuthorInfo(data);
      });
    }
  }, [selectedEntrepreneur]);

  const onShowDetails = (entrepreneur) => {
    setSelectedEntrepreneur(entrepreneur);
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchEntrepreneurs();
    }
  }, [isLoggedIn]);

  const fetchEntrepreneurs = async (startAfterDoc) => {
    setLoading(true);
    let q = query(
      collection(db, "entrepreneurs"),
      where("profileVisibility", "==", "1"),
      limit(ITEMS_PER_PAGE)
    );

    if (startAfterDoc) {
      q = query(q, startAfter(startAfterDoc));
    }

    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const newEntrepreneurs = await Promise.all(querySnapshot.docs.map(async (docSnapshot) => {
        const entrepreneurData = docSnapshot.data();
        const userRef = doc(db, 'users', entrepreneurData.userId);
        const userSnap = await getDoc(userRef);
        return {
          id: docSnapshot.id,
          ...entrepreneurData,
          userName: userSnap.exists() ? userSnap.data().firstName : 'Unknown',
          userAvatar: userSnap.exists() ? userSnap.data().avatar : null
        };
      }));

      setEntrepreneurs(prev => [...prev, ...newEntrepreneurs]);
      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastVisible(lastDoc);
      setHasMore(querySnapshot.docs.length === ITEMS_PER_PAGE);
    } else {
      setHasMore(false);
    }

    setLoading(false);
  };

  const loadMoreEntrepreneurs = () => {
    if (lastVisible) {
      fetchEntrepreneurs(lastVisible);
    }
  };

  const handleToggleCategory = (categoryKey) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryKey) ? prev.filter((c) => c !== categoryKey) : [...prev, categoryKey]
    );
  };

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedState('');
    setCityFilter('');
    setShowHighlightsOnly(false);
  };

  const filteredEntrepreneurs = useMemo(() => {
    const cityLower = cityFilter.trim().toLowerCase();
    return entrepreneurs.filter((e) => {
      const categories = Array.isArray(e.categories) && e.categories.length > 0
        ? e.categories
        : (e.category ? [e.category] : []);
      if (selectedCategories.length > 0 && !categories.some((c) => selectedCategories.includes(c))) {
        return false;
      }
      if (selectedState && e.businessState !== selectedState) return false;
      if (cityLower && (!e.businessCity || !e.businessCity.toLowerCase().includes(cityLower))) {
        return false;
      }
      if (showHighlightsOnly && e.profileHighlight !== '1') return false;
      return true;
    });
  }, [entrepreneurs, selectedCategories, selectedState, cityFilter, showHighlightsOnly]);

  return (
    <>
      <Menu isLoggedIn={isLoggedIn} />
      <WeeklyHighlights showJoinUsButton={false} />
      <div className="entrepreneurs-container">
        <EntrepreneursFilterBar
          selectedCategories={selectedCategories}
          onToggleCategory={handleToggleCategory}
          selectedState={selectedState}
          onStateChange={setSelectedState}
          cityFilter={cityFilter}
          onCityChange={setCityFilter}
          showHighlightsOnly={showHighlightsOnly}
          onToggleHighlights={setShowHighlightsOnly}
          onClear={handleClearFilters}
        />
        <EntrepreneursGrid
          entrepreneurs={filteredEntrepreneurs}
          loading={loading}
          hasMore={hasMore && filteredEntrepreneurs.length === entrepreneurs.length}
          loadMoreEntrepreneurs={loadMoreEntrepreneurs}
          onShowDetails={onShowDetails}
        />
        {selectedEntrepreneur && (
          <EntrepreneurDetailsPopup
            entrepreneur={selectedEntrepreneur}
            onClose={() => setSelectedEntrepreneur(null)}
            authorInfo={authorInfo}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default Entrepreneurs;
